import { BasecampSdk, initBasecampHTTPClient } from '@condo/sdk/basecamp';
import { CalcengineSdkWrapper, initCalcengineHTTPClient } from '@condo/sdk/calcengine';
import { initPropxHTTPClient, PropxSdk } from '@condo/sdk/propx';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import qs from 'qs';
import { v4 as uuid } from 'uuid';
import type { IPublicRamConfig, IRamConfig, IRamRuntimeConfig } from '@/shared/env/types';
import i18n, { type RamLanguage } from '@/shared/i18n/instance';
import { authManager } from '@/shared/utils/auth/auth-manager';
import { getFirebaseAuth } from '@/shared/utils/firebase/firebase';
import { readEffectiveImpersonatedUserId } from '@/shared/utils/impersonation-storage';
import { getPersistedLanguage, normalizeRamLanguage } from '@/shared/utils/language-persistence';
import { handleApiError } from '@/shared/api/error-handler';

export interface IApiClients {
    basecamp?: AxiosInstance;
    basecampSdk?: BasecampSdk;
    aiHub?: AxiosInstance;
    calcengine?: CalcengineSdkWrapper;
    propx?: PropxSdk;
}

type RamAxiosRequestConfig = AxiosRequestConfig & {
    skipGlobalErrorToast?: boolean;
};

declare global {
    interface Window {
        __CLIENTS__: IApiClients;
    }
}

// Client instances
const clients: IApiClients = {
    basecamp: import.meta?.hot?.data?.basecamp ?? window.__CLIENTS__?.basecamp,
    basecampSdk: import.meta?.hot?.data?.basecampSdk ?? window.__CLIENTS__?.basecampSdk,
    aiHub: import.meta?.hot?.data?.aiHub ?? window.__CLIENTS__?.aiHub,
    calcengine: import.meta?.hot?.data?.calcengine ?? window.__CLIENTS__?.calcengine,
    propx: import.meta?.hot?.data?.propx ?? window.__CLIENTS__?.propx,
};

// Initialize clients on window for HMR support
window.__CLIENTS__ = window.__CLIENTS__ ?? clients;

const isDev = import.meta.env.DEV;

export function getCurrentAcceptLanguageHeader(): RamLanguage {
    return normalizeRamLanguage(i18n.resolvedLanguage) ?? normalizeRamLanguage(i18n.language) ?? getPersistedLanguage();
}

/**
 * Request interceptor to add auth token and other headers
 */
const onRequest = async <T extends AxiosRequestConfig>(config: T): Promise<T> => {
    config.headers = config.headers ?? {};

    config.headers['Accept-Language'] = getCurrentAcceptLanguageHeader();

    // Add stream ID for tracking
    config.headers['x-stream-id'] = uuid();

    // Add auth token
    try {
        const auth = getFirebaseAuth();
        if (auth.currentUser) {
            const token = await authManager.getToken(false);
            config.headers.Authorization = `Bearer ${token}`;
        }
    } catch (error) {
        if (isDev) {
            console.error('Failed to get auth token:', error);
        }
    }

    const impersonatedUid = readEffectiveImpersonatedUserId();
    if (impersonatedUid) {
        config.headers['x-impersonate-user'] = impersonatedUid;
    }

    return config;
};

function resolveServiceNameFromBaseUrl(baseURL: string | undefined): string {
    if (!baseURL) {
        return 'API';
    }
    try {
        const url = new URL(baseURL);
        const hostnameParts = url.hostname.split('.');
        if (hostnameParts.length > 2) {
            return hostnameParts[0];
        }
        return url.hostname;
    } catch {
        return 'API';
    }
}

/**
 * Response error interceptor
 */
const onRejectedResponse = async (error: unknown): Promise<never> => {
    if (axios.isCancel(error)) {
        return Promise.reject(error);
    }

    const axiosError = axios.isAxiosError(error) ? error : null;
    const serviceName = resolveServiceNameFromBaseUrl(axiosError?.config?.baseURL);

    const skipToast = (axiosError?.config as RamAxiosRequestConfig | undefined)?.skipGlobalErrorToast;
    if (!skipToast) {
        await handleApiError(error, serviceName);
    }

    if (isDev && axiosError?.response?.status === 401) {
        console.error('Unauthorized request:', error);
    }

    return Promise.reject(error);
};

const getAxiosClient = (baseUrl: string) => {
    const client = axios.create({
        baseURL: baseUrl,
        timeout: 30000,
        paramsSerializer: {
            serialize: params => qs.stringify(params, { arrayFormat: 'repeat' }),
        },
    });

    client.interceptors.request.use(onRequest);
    client.interceptors.response.use(response => response, onRejectedResponse);

    return client;
};

/**
 * Initialize Basecamp API client
 */
export const initBasecampApiClient = (config: IPublicRamConfig | IRamRuntimeConfig): AxiosInstance => {
    if (!clients.basecamp) {
        const baseUrl = config.S_BASECAMP_API_URL || 'http://127.0.0.1:3001';
        clients.basecamp = getAxiosClient(baseUrl);
    }

    return clients.basecamp;
};

/**
 * Initialize Basecamp SDK client
 */
export const initBasecampSdkClient = (config: IPublicRamConfig | IRamRuntimeConfig): BasecampSdk => {
    if (!clients.basecampSdk) {
        const baseUrl = config.S_BASECAMP_API_URL || 'http://127.0.0.1:3001';
        const basecampHttpClient = initBasecampHTTPClient(baseUrl, onRequest, onRejectedResponse);
        clients.basecampSdk = new BasecampSdk(basecampHttpClient);
    }

    return clients.basecampSdk;
};

export const initAiHubApiClient = (config: IPublicRamConfig | IRamRuntimeConfig): AxiosInstance => {
    if (!clients.aiHub) {
        const baseUrl = config.S_AI_HUB_API_URL || 'http://127.0.0.1:3036';
        clients.aiHub = getAxiosClient(baseUrl);
    }

    return clients.aiHub;
};

export const initCalcengineApiClient = (config: IPublicRamConfig | IRamRuntimeConfig): CalcengineSdkWrapper => {
    if (!clients.calcengine) {
        const baseUrl = config.S_CALCENGINE_URL || 'http://127.0.0.1:3010';
        const calcengineClient = initCalcengineHTTPClient(baseUrl, onRequest, onRejectedResponse);
        clients.calcengine = new CalcengineSdkWrapper(calcengineClient);
    }

    return clients.calcengine;
};

/**
 * Initialize PropX API client
 */
export const initPropxApiClient = (config: IRamConfig): PropxSdk => {
    if (!clients.propx) {
        const baseUrl = config.S_PROPX_API_URL || 'http://127.0.0.1:3024';

        const propxClient = initPropxHTTPClient(baseUrl, onRequest, onRejectedResponse);

        clients.propx = new PropxSdk(propxClient);
    }

    return clients.propx;
};

/**
 * Initialize clients needed before the user is authenticated.
 */
export const initPublicHttpClients = (config: IPublicRamConfig | IRamRuntimeConfig): void => {
    initBasecampApiClient(config);
    initBasecampSdkClient(config);
    initAiHubApiClient(config);
    initCalcengineApiClient(config);
};

/**
 * Initialize all authenticated HTTP clients.
 */
export const initHttpClients = (config: IRamConfig): void => {
    initPublicHttpClients(config);
    initPropxApiClient(config);
};

/**
 * Get a specific client by identifier
 */
export const getClient = <TIdentifier extends keyof IApiClients>(identifier: TIdentifier): NonNullable<IApiClients[TIdentifier]> => {
    const client = clients[identifier];

    if (!client) {
        throw new Error(`Client ${identifier} is not initialized`);
    }

    return client;
};

/**
 * Reset all clients (useful when auth state changes)
 */
export const resetClients = (): void => {
    clients.basecamp = undefined;
    clients.basecampSdk = undefined;
    clients.aiHub = undefined;
    clients.calcengine = undefined;
    clients.propx = undefined;
    if (isDev) {
        console.debug('API clients reset');
    }
};

// Hot module replacement support
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        if (import.meta.hot?.data) {
            import.meta.hot.data.clients = clients;
        }
    });
}
